import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateDepartment } from '../../hooks/departments/useCreateDepartment';
import { useUpdateDepartment } from '../../hooks/departments/useUpdateDepartment';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditDepartment = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createDepartment, isPending: isCreating } = useCreateDepartment();
    const { editDepartment, isUpdate } = useUpdateDepartment();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { departments: allDepartments } = useGetDepartments(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '',
        parent_id: '',
        code: '',
        name: '',
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            parent_id: currentData.parent_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            school_id: Number(form.school_id),
            parent_id: form.parent_id ? Number(form.parent_id) : null,
            code: form.code || null,
            name: form.name,
            is_active: !!form.is_active,
        };

        if (isEdit && currentData?.id) {
            editDepartment({ req: payload, id: currentData.id });
        } else {
            createDepartment(payload);
        }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOptions = (schools || []).map((s) => ({ id: s.id, name: s.name }));

    // Filter parent options: same school + exclude self in edit mode
    const parentOptions = (allDepartments || [])
        .filter((d) => {
            if (form.school_id && d.school_id !== Number(form.school_id)) return false;
            if (isEdit && currentData?.id && d.id === currentData.id) return false;
            return true;
        })
        .map((d) => ({ id: d.id, name: d.name }));

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'المدرسة' : 'School'}
                            type="select"
                            name="school_id"
                            value={form.school_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, school_id: e.target.value, parent_id: '' }))}
                            options={schoolOptions}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'القسم الأب (اختياري)' : 'Parent Department (optional)'}
                            type="select"
                            name="parent_id"
                            value={form.parent_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, parent_id: e.target.value }))}
                            options={[{ id: '', name: isArabic ? 'بدون قسم أب' : 'No parent' }, ...parentOptions]}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود القسم' : 'Department Code'}
                            placeholder={isArabic ? 'مثال: DEP-01' : 'e.g. DEP-01'}
                            type="text"
                            name="code"
                            value={form.code}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم القسم' : 'Department Name'}
                            placeholder={isArabic ? 'ادخل اسم القسم' : 'Enter department name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_active}
                                        name="is_active"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic ? 'جاري الحفظ...' : 'Saving...'
                            : isEdit
                                ? isArabic ? 'تحديث' : 'Update'
                                : isArabic ? 'حفظ' : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditDepartment;
